import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import DateSeparator from './DateSeparator';
import MessageGroup from './MessageGroup';
import EmptyChatState from './EmptyChatState';
import type { ChatMessage, ChatRoom } from '../../types/chat';
import { useCurrentChatIdentity } from '../../hooks/chat/useCurrentChatIdentity';
import { parseApiDate } from '../../utils/apiDates';

interface MessageThreadProps {
  room: ChatRoom | undefined;
  messages: ChatMessage[];
  isLoading?: boolean;
  isFetchingPrevious?: boolean;
  hasPrevious?: boolean;
  onLoadPrevious?: () => void;
  onEdit?: (messageId: string) => void;
  onDelete?: (messageId: string) => void;
}

interface RenderItem {
  type: 'separator' | 'group';
  key: string;
  date?: Date;
  messages?: ChatMessage[];
  senderId?: string;
}

function buildItems(messages: ChatMessage[]): RenderItem[] {
  if (!messages.length) return [];
  const items: RenderItem[] = [];
  let currentDayKey = '';
  let currentSender = '';
  let bucket: ChatMessage[] = [];
  const flushBucket = () => {
    if (bucket.length) {
      items.push({
        type: 'group',
        key: `g-${bucket[0].id}`,
        senderId: currentSender,
        messages: bucket,
      });
      bucket = [];
    }
  };
  for (const m of messages) {
    const d = parseApiDate(m.created_at);
    const dayKey = d.toISOString().slice(0, 10);
    if (dayKey !== currentDayKey) {
      flushBucket();
      items.push({ type: 'separator', key: `sep-${dayKey}`, date: d });
      currentDayKey = dayKey;
      currentSender = '';
    }
    if (m.sender_id !== currentSender) {
      flushBucket();
      currentSender = m.sender_id;
    }
    bucket.push(m);
  }
  flushBucket();
  return items;
}

const MessageThread: React.FC<MessageThreadProps> = ({
  room,
  messages,
  isLoading,
  isFetchingPrevious,
  hasPrevious,
  onLoadPrevious,
  onEdit,
  onDelete,
}) => {
  const { userId } = useCurrentChatIdentity();
  const containerRef = useRef<HTMLDivElement>(null);
  const [stickyBottom, setStickyBottom] = useState(true);
  const previousScrollHeight = useRef(0);

  const items = useMemo(() => buildItems(messages), [messages]);
  const memberById = useMemo(() => {
    const map = new Map<string, ChatRoom['members'][number]>();
    if (room) {
      for (const m of room.members) {
        if (m.user_id) map.set(m.user_id.toUpperCase(), m);
      }
    }
    return map;
  }, [room]);

  // Keep stickiness when user is near the bottom; release when they scroll up.
  const onScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setStickyBottom(distanceFromBottom < 80);
    if (el.scrollTop < 200 && hasPrevious && !isFetchingPrevious && onLoadPrevious) {
      previousScrollHeight.current = el.scrollHeight;
      onLoadPrevious();
    }
  };

  // Preserve scroll offset after prepending older messages.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el || !previousScrollHeight.current) return;
    const delta = el.scrollHeight - previousScrollHeight.current;
    if (delta > 0) el.scrollTop += delta;
    previousScrollHeight.current = 0;
  }, [messages.length]);

  // Auto-scroll to bottom on new messages when sticky.
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !stickyBottom) return;
    el.scrollTop = el.scrollHeight;
  }, [messages.length, stickyBottom]);

  if (!room && !isLoading) {
    return <EmptyChatState />;
  }
  if (messages.length === 0 && !isLoading) {
    return <EmptyChatState />;
  }

  return (
    <div
      ref={containerRef}
      onScroll={onScroll}
      className="flex-1 overflow-y-auto px-4 py-4 scrollbar-thin"
      aria-live="polite"
      aria-relevant="additions"
    >
      {isFetchingPrevious && (
        <p className="text-center text-xs text-text-tertiary py-2">Loading older messages…</p>
      )}
      {items.map((item) => {
        if (item.type === 'separator' && item.date) {
          return <DateSeparator key={item.key} date={item.date} />;
        }
        const senderUpper = (item.senderId ?? '').toUpperCase();
        const selfUpper = (userId ?? '').toUpperCase();
        return (
          <MessageGroup
            key={item.key}
            messages={item.messages ?? []}
            sender={memberById.get(senderUpper)}
            isSelf={!!selfUpper && senderUpper === selfUpper}
            room={room}
            selfId={userId}
            onEdit={onEdit}
            onDelete={onDelete}
          />
        );
      })}
    </div>
  );
};

export default MessageThread;
